import type { Locale } from "@src/i18n/locale-catalog";
import type { ShareDialog } from "@src/features/sharing/ShareDialogs";
import { synchronizeLocaleFromLocation } from "../locale-navigation";
import { decodeShareDocument } from "@src/features/sharing/codec/codec";
import { useEffect, useEffectEvent } from "react";
import type { AppStore } from "../state/app-store";
import type { TransformationViewportHandle } from "@src/features/visualization/TransformationViewport";
import type { RefObject } from "react";

export function useShareRoute({
  store,
  scene,
  setDialog,
  navigationAttemptRef,
  routedHashRef,
  onLocaleChange,
  setReady,
}: {
  store: AppStore;
  scene: RefObject<TransformationViewportHandle | null>;
  setDialog: (dialog: ShareDialog | null) => void;
  navigationAttemptRef: RefObject<number>;
  routedHashRef: RefObject<string | null>;
  onLocaleChange?: ((locale: Locale) => void) | undefined;
  setReady: (ready: boolean) => void;
}): void {
  const notifyLocaleChange = useEffectEvent((next: Locale) => {
    onLocaleChange?.(next);
  });
  useEffect(() => {
    let active = true;
    const navigate = async (force = false) => {
      if (synchronizeLocaleFromLocation(notifyLocaleChange)) setDialog(null);
      const hash = window.location.hash;
      if (!force && hash === routedHashRef.current) return;
      routedHashRef.current = hash;
      const attempt = ++navigationAttemptRef.current;
      const isCurrentRoute = () =>
        active &&
        attempt === navigationAttemptRef.current &&
        window.location.hash === hash;
      store.getState().cancelShareLoad();
      let restored = false;
      if (hash.startsWith("#s=")) {
        setReady(false);
        restored = await loadSharedDocument(
          hash,
          isCurrentRoute,
          force,
          store,
          scene,
          setDialog,
        );
      } else if (!force) setDialog(null);
      if (!isCurrentRoute()) return;
      if (restored) {
        clearLocationHash();
        routedHashRef.current = "";
      }
      setReady(true);
    };
    const onLocationChange = () => void navigate();
    window.addEventListener("hashchange", onLocationChange);
    window.addEventListener("popstate", onLocationChange);
    void navigate(true);
    return () => {
      active = false;
      store.getState().cancelShareLoad();
      window.removeEventListener("hashchange", onLocationChange);
      window.removeEventListener("popstate", onLocationChange);
    };
  }, [navigationAttemptRef, routedHashRef, scene, setDialog, setReady, store]);
}

async function loadSharedDocument(
  hash: string,
  isActive: () => boolean,
  resetOnError: boolean,
  store: AppStore,
  scene: RefObject<TransformationViewportHandle | null>,
  setDialog: (dialog: ShareDialog | null) => void,
): Promise<boolean> {
  try {
    const shareDocument = await decodeShareDocument(hash.slice(3));
    if (!isActive()) return false;
    await store.getState().loadShareDocument(shareDocument);
    if (!isActive()) return false;
    scene.current?.restoreCameraSnapshots(shareDocument.cameras);
    setDialog(null);
    return true;
  } catch {
    if (!isActive()) return false;
    if (resetOnError) {
      store.getState().resetApp();
      scene.current?.resetView(2);
      scene.current?.resetView(3);
    }
    setDialog("share-error");
    return false;
  }
}

export function clearLocationHash(): void {
  const url = new URL(window.location.href);
  url.hash = "";
  window.history.replaceState(window.history.state, "", url);
}
